import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cardinal } from '../types';

// La liste des papabili est maintenant déterminée par la propriété isPapabili dans les données

interface PapabiliListProps {
  cardinaux: Cardinal[];
}

const PapabiliList: React.FC<PapabiliListProps> = ({ cardinaux }) => {
  // Fonction pour naviguer vers la page d'un cardinal
  const navigateToCardinal = (cardinal: Cardinal) => {
    // Créer un slug à partir du nom du cardinal
    const slug = cardinal.name.toLowerCase().replace(/\s+/g, '-');
    window.location.href = `/cardinal/${slug}`;
  };
  // Fonction pour déterminer quel pape a créé le cardinal
  const getCreatorPope = (cardinal: Cardinal): string => {
    if (cardinal.createdByFrancis) {
      return "Pape François";
    } else {
      // Déterminer le pape en fonction de la date de création
      const creationDate = cardinal.creationDate ? new Date(cardinal.creationDate.split('/').reverse().join('-')) : null;
      
      if (creationDate) {
        // Benoît XVI: 19 avril 2005 - 28 février 2013
        const benedictStart = new Date('2005-04-19');
        const benedictEnd = new Date('2013-02-28');
        
        // Jean-Paul II: 16 octobre 1978 - 2 avril 2005
        const jpiiStart = new Date('1978-10-16');
        const jpiiEnd = new Date('2005-04-02');
        
        if (creationDate >= benedictStart && creationDate <= benedictEnd) {
          return "Benoît XVI";
        } else if (creationDate >= jpiiStart && creationDate <= jpiiEnd) {
          return "Jean-Paul II";
        }
      }
      
      return "Autre pape";
    }
  };
  // Fonction pour vérifier si un cardinal est un papabile
  const isPapabile = (cardinal: Cardinal): boolean => {
    // Utiliser directement la propriété isPapabili du cardinal
    return cardinal.isPapabili === true;
  };

  // Log pour débogage
  console.log("Tous les cardinaux:", cardinaux.map(c => c.name));
  
  // Filtrer les cardinaux qui sont marqués comme papabili dans les données
  const papabiliCardinaux: Cardinal[] = cardinaux.filter(cardinal => {
    const isPapabiliCardinal = isPapabile(cardinal);
    if (isPapabiliCardinal) {
      console.log(`Cardinal papabile trouvé: ${cardinal.name}`);
    }
    return isPapabiliCardinal;
  });
  
  console.log("Papabili cardinaux trouvés:", papabiliCardinaux.map(c => c.name));
  
  return (
    <div className="container mx-auto px-2 sm:px-4">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-6 sm:mb-10"
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
          Les 18 Papabili des médias
        </h1>
        <p className="text-gray-300 mt-3 sm:mt-4 max-w-3xl mx-auto text-sm sm:text-base px-2">
          Les "papabili" sont les cardinaux considérés par les médias comme ayant le plus de chances d'être élus pape lors d'un conclave.
        </p>
        <p className="text-gray-300 mt-2max-w-3xl mx-auto text-sm sm:text-base px-2">
          Mais ce n'est que le regard du monde avec parfois ses rivalités, ses convoitises et ses séductions ! N'oublions pas le dicton : « celui qui entre pape au conclave en ressort cardinal ».
        </p>
        <p className="text-gray-300 mt-3 max-w-3xl mx-auto font-bold text-lg sm:text-base">
          « L'homme regarde l'apparence, mais le Seigneur regarde le cœur. » (1 Samuel 16, 7)
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        {papabiliCardinaux.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-10">
            Aucun cardinal papabile trouvé.
          </div>
        ) : (
          <>
            {papabiliCardinaux.map((cardinal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => navigateToCardinal(cardinal)}
              >
                <div className="p-4 sm:p-6">
                  <h2 className="text-xl font-bold underline text-yellow-500 mb-2 text-center">{cardinal.name}</h2>
                  {cardinal.imageUrl && (
                    <div className="mb-4 flex justify-center">
                      <img 
                        src={cardinal.imageUrl} 
                        alt={cardinal.name} 
                        className="w-32 h-32 object-cover rounded-full border-2 border-yellow-500"
                      />
                    </div>
                  )}
                  {cardinal.function && (
                    <p className="text-yellow-500 mb-2 text-center">
                      {cardinal.function}
                    </p>
                  )}
                  {cardinal.country && (
                    <p className="text-gray-300 mb-2">
                      <span className="font-semibold">Pays:</span> {cardinal.country}
                    </p>
                  )}
                  {cardinal.age && (
                    <p className="text-gray-300 mb-2">
                      <span className="font-semibold">Âge:</span> {cardinal.age} ans
                    </p>
                  )}
                  {cardinal.creationDate && (
                    <p className="text-gray-300 mb-2">
                      <span className="font-semibold">Créé cardinal par:</span> {getCreatorPope(cardinal)}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default PapabiliList;
